// src/AllPartsList.js
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";

const COLS = [
  "Part Number",
  "Description",
  "Total Quantity",
  "UOM",
  "Location",
  "Shelve",
  "Row",
  "Category",
  "Manufacturer Part#",
  "Manufacturer",
  "Supplier",
  "Department",
  "Project Job Number",
  "Equipment",
];

function getUom(p) {
  // adapt to whatever your DB uses
  return (
    p["UOM"] ||
    p["UOM Data"] ||
    p["Received UOM"] ||
    p["Units Per Box UMO"] ||
    ""
  );
}

export default function AllPartsList({ site }) {
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [rawParts, setRawParts] = useState([]);
  const [parts, setParts] = useState([]);

  const [err, setErr] = useState("");

  const [filters, setFilters] = useState({
    "Part Number": "",
    "Description": "",
    "Location": "All",
    "Category": "All",
    "Manufacturer": "All",
    "Supplier": "All",
    "Department": "All",
    "Project Job Number": "All",
    "Equipment": "All",
  });

  // Reset when site changes
  useEffect(() => {
    setLoaded(false);
    setLoading(false);
    setErr("");
    setRawParts([]);
    setParts([]);
    setFilters({
      "Part Number": "",
      "Description": "",
      "Location": "All",
      "Category": "All",
      "Manufacturer": "All",
      "Supplier": "All",
      "Department": "All",
      "Project Job Number": "All",
      "Equipment": "All",
    });
  }, [site]);

  // Build dropdown values from loaded parts (like Tkinter)
  const dropdownOptions = useMemo(() => {
    const make = (key) => {
      const vals = new Set();
      for (const p of rawParts) {
        const v = (p?.[key] ?? "").toString().trim();
        if (v) vals.add(v);
      }
      return ["All", ...Array.from(vals).sort((a, b) => a.localeCompare(b))];
    };

    return {
      Location: make("Location"),
      Category: make("Category"),
      Manufacturer: make("Manufacturer"),
      Supplier: make("Supplier"),
      Department: make("Department"),
      "Project Job Number": make("Project Job Number"),
      Equipment: make("Equipment"),
    };
  }, [rawParts]);

  const applyFilters = (base) => {
    const f = filters;

    const pn = f["Part Number"].trim().toLowerCase();
    const desc = f["Description"].trim().toLowerCase();

    const dropdownKeys = [
      "Location",
      "Category",
      "Manufacturer",
      "Supplier",
      "Department",
      "Project Job Number",
      "Equipment",
    ];

    const out = base.filter((p) => {
      const pnum = (p["Part Number"] ?? "").toString().toLowerCase();
      const pdesc = (p["Description"] ?? p.Description ?? "").toString().toLowerCase();

      if (pn && !pnum.includes(pn)) return false;
      if (desc && !pdesc.includes(desc)) return false;

      for (const k of dropdownKeys) {
        const want = (f[k] ?? "All").toString();
        if (want === "All") continue;
        const cell = (p?.[k] ?? "").toString();
        if (!cell) return false;
        // match like Tkinter: substring match
        if (!cell.toLowerCase().includes(want.toLowerCase())) return false;
      }

      return true;
    });

    setParts(out);
  };

  // Apply filters whenever filters change AFTER Load is clicked
  useEffect(() => {
    if (!loaded) return;
    applyFilters(rawParts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, loaded, rawParts]);

  const loadParts = async () => {
    if (!site) return;
    setLoading(true);
    setErr("");

    try {
      // backend limit max is 1000 right now
      const params = new URLSearchParams({ site, limit: "1000" });
      const res = await apiFetch(`/api/parts/search?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = (await res.json()) || [];
      setRawParts(data);
      setLoaded(true);

      // first render uses current filters (defaults => show all)
      setParts(data);
    } catch (e) {
      console.error(e);
      setErr("Failed to load parts");
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      "Part Number": "",
      "Description": "",
      "Location": "All",
      "Category": "All",
      "Manufacturer": "All",
      "Supplier": "All",
      "Department": "All",
      "Project Job Number": "All",
      "Equipment": "All",
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  return (
    <div className="p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <h2 className="text-2xl font-semibold">All Parts @ {site}</h2>

        <div className="flex gap-2">
          <button
            onClick={loadParts}
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={loading || !site}
          >
            {loading ? "Loading…" : "Load Parts"}
          </button>

          <button
            onClick={clearFilters}
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            disabled={!loaded}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Tip: Click <b>Load Parts</b> first, then use filters. (Max 1000 rows until we add paging.)
      </p>

      {err && <p className="mb-3 text-red-600">{err}</p>}

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <input
          name="Part Number"
          value={filters["Part Number"]}
          onChange={onChange}
          placeholder="Part Number"
          className="border rounded px-2 py-2"
          disabled={!loaded}
        />

        <input
          name="Description"
          value={filters["Description"]}
          onChange={onChange}
          placeholder="Description"
          className="border rounded px-2 py-2"
          disabled={!loaded}
        />

        {[
          "Location",
          "Category",
          "Manufacturer",
          "Supplier",
          "Department",
          "Project Job Number",
          "Equipment",
        ].map((k) => (
          <select
            key={k}
            name={k}
            value={filters[k]}
            onChange={onChange}
            className="border rounded px-2 py-2"
            disabled={!loaded}
          >
            {(dropdownOptions[k] || ["All"]).map((v) => (
              <option key={v} value={v}>
                {k}: {v}
              </option>
            ))}
          </select>
        ))}
      </div>

      {/* Table */}
      {!loaded ? (
        <p className="text-gray-700">Click <b>Load Parts</b> to display parts.</p>
      ) : (
        <div className="text-sm text-gray-600 mb-2">
          Showing <b>{parts.length}</b> of <b>{rawParts.length}</b>
        </div>
      )}

      {loaded && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {COLS.map((h) => (
                  <th key={h} className="border p-2 bg-gray-100 text-left whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parts.map((p, idx) => (
                <tr key={`${p["Part Number"]}-${idx}`} className="hover:bg-gray-50">
                  <td className="border p-2 font-mono">{p["Part Number"]}</td>
                  <td className="border p-2">{p.Description}</td>
                  <td className="border p-2">{p["Total Quantity"]}</td>
                  <td className="border p-2">{getUom(p)}</td>
                  <td className="border p-2">{p.Location}</td>
                  <td className="border p-2">{p.Shelve}</td>
                  <td className="border p-2">{p.Row}</td>
                  <td className="border p-2">{p.Category}</td>
                  <td className="border p-2">{p["Manufacturer Part#"]}</td>
                  <td className="border p-2">{p.Manufacturer}</td>
                  <td className="border p-2">{p.Supplier}</td>
                  <td className="border p-2">{p.Department}</td>
                  <td className="border p-2">{p["Project Job Number"]}</td>
                  <td className="border p-2">{p.Equipment}</td>
                </tr>
              ))}

              {loaded && parts.length === 0 && (
                <tr>
                  <td className="border p-2" colSpan={COLS.length}>
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
